import videojs from 'video.js';

import './seekbar-segment.css';

import SeekBarSegmentProgressBar from './seekbar-segment-progress-bar.js'

const _startTime = new WeakMap();
const _endTime = new WeakMap();
const _label = new WeakMap();
const _spacerWidth = new WeakMap();
const _progress = new WeakMap();

const DEFAULT_SPACER_WIDTH_PIXELS = 6;

export default class SeekBarSegment extends videojs.getComponent('Component') {
    /**
     * 
     * @param {videojs.Player} player
     * @param {Object} options
     * @param {number} options.startTime
     * @param {number} options.endTime
     * @param {string} options.label
     * @param {number} options.spacerWidth
     * @param {boolean} options.progress
     */
    constructor(player, options) {
        super(player, options);

        player.ready(() => {
            const { startTime, endTime, label, spacerWidth, progress } = options;
            this.startTime = startTime;
            this.endTime = endTime;
            this.label = label;
            this.spacerWidth = spacerWidth ?? DEFAULT_SPACER_WIDTH_PIXELS;
            this.progress = progress;

            this.createSegment();

            const resizeOb = new ResizeObserver(entries => {
                for (const entry of entries) {
                    this.onResize();
                }
            })


            resizeOb.observe(this.player().getChild('ControlBar').getChild('ProgressControl').getChild('SeekBar').getChild('PlayProgressBar').el());
        });
    }

    onResize() {
        this.createSegment();
    }

    set hidden(hidden) {
        if (hidden) {
            this.addClass('vjs-hidden');
        }
        else {
            this.removeClass('vjs-hidden');
        }
    }

    get hidden() {
        return this.hasClass('vjs-hidden');
    }

    set startTime(startTime) {
        _startTime.set(this, startTime);
        this.label = this.label;
    }

    get startTime() {
        return _startTime.get(this);
    }

    set endTime(endTime) {
        _endTime.set(this, endTime);
        this.label = this.label;
    }

    get endTime() {
        return _endTime.get(this);
    }

    set label(label) {
        _label.set(this, label);
        const el = this.el();

        if (label) {
            el.style.setProperty('--tooltip-content', `"${videojs.formatTime(this.startTime)} - ${label}"`);
            el.setAttribute('aria-label', label);
        } else {
            el.style.setProperty('--tooltip-content', `"${videojs.formatTime(this.startTime)}`);
        }
    }

    get label() {
        return _label.get(this);
    }

    set spacerWidth(spacerWidth) {
        _spacerWidth.set(this, spacerWidth);
    }

    get spacerWidth() {
        return _spacerWidth.get(this);
    }

    set progress(progress) {
        _progress.set(this, progress);
        if (progress === true) {
            this.updateSeekBarStyles();
            this.addProgressBar();
        } else {
            this.updateSeekBarStyles();
            this.removeProgressBar();
        }
    }

    get progress() {
        return _progress.get(this);
    }

    updateSeekBarStyles() {
        const seekBar = this.player().getChild('ControlBar').getChild('ProgressControl').getChild('SeekBar').el();
        const playProgressBar = this.player().getChild('ControlBar').getChild('ProgressControl').getChild('SeekBar').getChild('PlayProgressBar').el();
        if (this.progress === true) {
            seekBar.style.backgroundColor = 'transparent';
            seekBar.style.overflowX = 'visible';
            playProgressBar.style.backgroundColor = 'transparent';
        } else {
            seekBar.style.backgroundColor = '#676767';
            seekBar.style.overflowX = 'hidden';
            playProgressBar.style.backgroundColor = 'var(--player-primary-color)';
        }
    }

    addProgressBar() {
        const player = this.player();

        const startTime = this.startTime;
        const endTime = this.endTime;

        const seekBarSegmentProgressBar = new SeekBarSegmentProgressBar(player, { name: 'SeekBarSegmentProgressBar', startTime, endTime });
        this.addChild(seekBarSegmentProgressBar);
    }

    removeProgressBar() {
        this.el().removeChild('SeekBarSegmentProgressBar');
    }

    createSegment() {
        const player = this.player();
        const seekBarWidth = player.getChild('ControlBar').getChild('ProgressControl').getChild('SeekBar').el().getBoundingClientRect().width;
        const presentationDuration = player.duration();
        const spacerWidth = this.spacerWidth;
        const spacerHalfWidth = spacerWidth / 2;
        const startPosition = this.startTime / presentationDuration;
        const endPosition = this.endTime / presentationDuration;
        //sometimes, the start/end positions math can lead to floating decimals, rather than a rounded number
        //rounding the positions messes up the positing with the timeline cursor, so new values are created for rounding
        const roundedStartPosition = this.#roundToHundredth(startPosition);
        const roundedEndPosition = this.#roundToHundredth(endPosition);
        
        const seekBarSegmentWidth = (seekBarWidth * (endPosition - startPosition));

        let segmentWidth;
        let segmentLeftPosition;

        if (roundedStartPosition === 0 && roundedEndPosition === 1) {
            segmentWidth = seekBarSegmentWidth;
            segmentLeftPosition = startPosition;
        } else if (seekBarSegmentWidth < spacerWidth) {
            segmentWidth = seekBarSegmentWidth;
            segmentLeftPosition = startPosition * seekBarWidth;
        } else if (roundedStartPosition === 0) {
            segmentWidth = (endPosition * seekBarWidth) - spacerHalfWidth;
            segmentLeftPosition = startPosition;
        } else if (roundedEndPosition === 1) {
            segmentWidth = seekBarWidth * (roundedEndPosition - startPosition);
            segmentLeftPosition = (startPosition * seekBarWidth) + spacerHalfWidth;
        } else {
            segmentWidth = seekBarSegmentWidth - spacerWidth;
            segmentLeftPosition = (startPosition * seekBarWidth) + spacerHalfWidth;
        }

        this.el().style.setProperty('--seekbar-width', seekBarWidth);
        this.el().style.setProperty('--seekbar-segment-width', segmentWidth);
        this.el().style.setProperty('--seekbar-segment-pos-left', segmentLeftPosition);
    }

    #roundToHundredth(num) {
        return Math.ceil(num * 100) / 100;
    }

    createEl(tag, props = {}, attributes = {}) {
        tag = 'div';
        props = Object.assign({
            className: 'mediasite-player__seekbar-segment'
        });

        return super.createEl.call(this, tag, props, attributes);
    }
}